import { Component, OnInit, effect, input, output, signal } from '@angular/core';
import { ITransactions, ITransactionsRes } from '../store/summaryOfChanges/state';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { ISubClient } from '../shared/types/shared-state';

export interface PagingEvent {
  pageSize: number;
  pageIndex: number;
}

export interface SessionSearchPayload {
  event: PagingEvent | null;
}

export interface DateSearchFormValue {
  startDate: string;
  endDate: string;
}

export interface DateSearchPayload {
  payers: string[];
  groupSpecifiedIdentifier: string;
  subgroupSpecifiedIdentifier: string;
  formValue: DateSearchFormValue;
  event: PagingEvent | null;
}

@Component({
    selector: 'app-summary-of-changes-page',
    templateUrl: './summary-of-changes.component.html',
    styleUrls: ['./summary-of-changes.component.scss'],
    standalone: false
})
export class SummaryOfChangesComponent implements OnInit {
  getTBySession = output<SessionSearchPayload>();
  getTBySearch = output<DateSearchPayload>();
  getDetails = output<ITransactions>();
  resetSearchClick = output<boolean>();
  readonly sessionRecords = input<ITransactionsRes>({ totalRecords: 0, transactions: [] });
  readonly searchTRecords = input<ITransactionsRes>({ totalRecords: 0, transactions: [] });
  readonly selectedAdminPlan = input('');
  dateForm: UntypedFormGroup;
  readonly isFetching = input(false);
  readonly backToResults = signal(false);
  readonly isSearched = signal(false);
  readonly showDetails = signal(false);
  resetButton: HTMLElement | null = null;
  readonly selectedSubClients = signal<ISubClient[]>([]);
  defaultPageSize = 25;
  readonly selectedHeader = signal<'session' | 'previousTransaction'>('session');
  readonly transactionsRecordsSignal = signal<ITransactionsRes>({ totalRecords: 0, transactions: [] });
  readonly transactionsSignal = signal<ITransactions[]>([]);

  constructor(private readonly fb: UntypedFormBuilder) {
    effect(() => {
      this.sessionRecords();
      this.searchTRecords();
      this.getPagingData();
    });
  }

  ngOnInit(): void {
    this.searchBySessionId();
    this.setDateForm();
  }

  searchBySessionId(): void {
    this.getTBySession.emit({ event: null });
  }

  setDateForm(): void {
    this.dateForm = this.fb.group({
      startDate: [ moment(new Date()).subtract(1, 'month').format('MM/DD/YYYY'), Validators.required ],
      endDate: [ moment(new Date()).format('MM/DD/YYYY'), Validators.required ],
    });
  }

  onSearchSubmitted(): void {
    const searchContext = this.resolveSearchContext();
    if (!searchContext || !this.dateForm?.valid) {
      return;
    }

    this.getTBySearch.emit({
      ...searchContext,
      formValue: this.dateForm.getRawValue(),
      event: null
    });
    this.isSearched.set(true);
  }

// gives selected group info
subClientSelected(event: ISubClient[] | null): void {
  this.selectedSubClients.set(event || []);
  this.dateForm.reset({
    startDate: moment(new Date()).subtract(1, 'month').format('MM/DD/YYYY'),
    endDate: moment(new Date()).format('MM/DD/YYYY')
  });
  this.backToResults.set(false);
}

  clientSearchReset(_event: unknown): void {
    this.selectedSubClients.set([]);
    this.resetSearchClick.emit(true);
    this.isSearched.set(false);
    this.transactionsRecordsSignal.set({ totalRecords: 0, transactions: [] });
    this.transactionsSignal.set([]);
  }

  backToSearchResults(): void {
    this.backToResults.set(true);
    this.isSearched.set(false);
    this.resetSearchClick.emit(true);
    this.transactionsRecordsSignal.set({ totalRecords: 0, transactions: [] });
    this.transactionsSignal.set([]);
  }

  toggle(selectedHeader: 'session' | 'previousTransaction'): void {
    this.selectedHeader.set(selectedHeader);
    this.isSearched.set(false);
    this.resetSearchResults();
    this.getPagingData();
    this.showDetails.set(false);
  }

  // To clear the client search results.
  resetSearchResults(): void {
    this.resetButton = document.querySelector('.client-search-buttons .rs-button');
    if (this.resetButton) {
      this.resetButton.click();
    }
  }

  pageChanged(event: PagingEvent): void {
    if (this.selectedHeader() === 'session') {
      this.getTBySession.emit({ event });
      return;
    }
    if (this.selectedHeader() === 'previousTransaction') {
      const searchContext = this.resolveSearchContext();
      if (!searchContext || !this.dateForm?.valid) {
        return;
      }
      this.getTBySearch.emit({
        ...searchContext,
        formValue: this.dateForm.getRawValue(),
        event
      });
    }
  }

  getPagingData(): void {
    const records = this.selectedHeader() === 'session' ? this.sessionRecords() : this.searchTRecords();
    const normalized = records || { totalRecords: 0, transactions: [] };
    this.transactionsRecordsSignal.set(normalized);
    this.transactionsSignal.set(normalized.totalRecords ? [...(normalized.transactions || [])] : []);
  }

  selectedTransaction(event: ITransactions): void {
    this.showDetails.set(true);
    this.getDetails.emit(event);
  }

  backToTransactions(_event: unknown): void {
    this.showDetails.set(false);
  }

  private resolveSearchContext(): Pick<DateSearchPayload, 'payers' | 'groupSpecifiedIdentifier' | 'subgroupSpecifiedIdentifier'> | null {
    const selectedSubClient: ISubClient | undefined = this.selectedSubClients()[0];
    const payer = `${selectedSubClient?.planAcronym ?? ''}`.trim();
    const groupSpecifiedIdentifier = `${selectedSubClient?.client?.specifiedId ?? ''}`.trim();
    const subgroupSpecifiedIdentifier = `${selectedSubClient?.specifiedId ?? ''}`.trim();

    if (!payer || !groupSpecifiedIdentifier || !subgroupSpecifiedIdentifier) {
      return null;
    }

    return {
      payers: [payer],
      groupSpecifiedIdentifier,
      subgroupSpecifiedIdentifier
    };
  }

}
